import type { CaseDetail } from '../portfolio';

const aizuCoinDesignGuidelineCase: CaseDetail = {
  id: 'aizu-coin-design-guideline',
  project: 'AIZU COIN ブランドデザインガイドライン',
  client: 'Regional Community Organization',
  year: '2024',
  category: 'Brand Design Guideline / VI / Character / UI',
  role: 'Brand Design / VI System / Character Design / UI Design / Typography / Motion',
  issue:
    '地域通貨ブランドとしての一貫した視覚的アイデンティティが構築されていなかった。ロゴ、キャラクター、色彩、書体、レイアウト、UI、モーションまで、あらゆる接点で統一されたブランド体験を設計する必要があった。',
  challenge:
    'ブランド戦略を、具体的なビジュアル・デザイン・UIルールに体系化すること。内部デザイナー・外部パートナーが同じ品質でブランドを展開できるガイドラインとして機能させること。',
  insight:
    'ブランドの「らしさ」は、個別のデザイン要素ではなく、体系化されたルールの中で初めて一貫性を保つ。ガイドラインは「制約」ではなく「創造の共通言語」である。',
  strategy:
    '「BRAND DESIGN GUIDELINE」として、ブランド戦略をG08〜G25の章構成で体系化。タイポグラフィ、キャラクター（NEWベコ）、ロゴ・タグライン、キービジュアル、イラスト・写真、レイアウト・グリッド・コンポーネント、UI・アクセシビリティ・モーション、デジタルバッジ、広告・プロモーション、店舗ツール・POP、SNS・バナー・動画、ウェブ・アプリ・CTA、クリエイティブチェックリスト・ガバナンスまで一貫したルールを設計。',
  experience:
    'ガイドラインを通じて、内部デザイナー・外部パートナーが同じ品質でブランドを展開できる体験を設計。すべてのコミュニケーション接点で統一されたブランド体験を実現。',
  output: 'ブランドデザインガイドライン（G08〜G25）、ロゴ規定、キャラクター規定、カラーシステム、タイポグラフィシステム、レイアウト・グリッド・コンポーネント、UI／アクセシビリティ／モーション、デジタルバッジシステム、広告・プロモーションルール、店舗ツール・POP・サイン・QRルール、SNS・バナー・動画ガイドライン、ウェブ・アプリ・CTAルール、クリエイティブチェックリスト・ガバナンス。',
  impact: 'ブランド品質の一貫性確保と、内外部でのブランド展開品質の均一化。デジタルとリアルのすべての接点で統一されたブランド体験を実現。',
  images: [
    {
      src: '/assets/migrated/a132cda120a976b7fb66.webp',
      caption: '表紙 — BRAND DESIGN GUIDELINE / AIZU COIN ブランドコミュニケーションプラン',
    },
    {
      src: '/assets/migrated/0b5c0b8732e56fa1be11.webp',
      caption: 'G08 Typography Guideline — 書体ガイドライン / 和文（游ゴシック Bold）・英文（Montserrat）の字体ルールと使用例',
    },
    {
      src: '/assets/migrated/c55d7286b34cce42f2c1.webp',
      caption: 'G09 NEW BEKO Character Identity — NEWベコ・キャラクターアイデンティティ / 会津の郷土玩具「赤べこ」をモチーフにした公式キャラクター',
    },
    {
      src: '/assets/migrated/ea123442eaae4d183e61.webp',
      caption: 'G10 NEW BEKO Usage & Variation — NEWベコ・使用ルール＆バリエーション / 基本ルール、カラーバリエーション、サイズバリエーション、セットアイテム、使用シーン、使用禁止例',
    },
    {
      src: '/assets/migrated/2ca935d313c66a213046.webp',
      caption: 'G11 Logo / Tagline / Brand Lockup — ロゴ・タグライン・ブランドロックアップ / ロゴの基本要素、タグライン、ロックアップバリエーション、カラーバリエーション、クリアスペース、使用禁止例',
    },
    {
      src: '/assets/migrated/817d36be211e2aa5e223.webp',
      caption: 'G12 Key Visual & Graphic System — キービジュアル＆グラフィックシステム / キービジュアルの考え方、メインKV、バリエーション、グラフィック要素、スタイル、使用シーン例',
    },
    {
      src: '/assets/migrated/b2556b1bad937fec5a77.webp',
      caption: 'G13 Icon / Illustration / Photography — アイコン／イラストレーション／フォトグラフィ / アイコンセット、イラストレーション（キャラクター・風景・文化・食）、フォトグラフィの使用テーマとトーン',
    },
    {
      src: '/assets/migrated/f8f81b878f68c59aa780.webp',
      caption: 'G14 Typography System — タイポグラフィシステム / 基本方針、書体の使い分け、書体の階層（サイズ・ウエイト・行間・カラー）、組み合わせ例、数字・記号のルール、使用上の注意',
    },
    {
      src: '/assets/migrated/9fa2e5ab49b9c29847de.webp',
      caption: 'G16 Layout / Grid / Components — レイアウト／グリッド／コンポーネント / グリッドシステム、スペーシングシステム、レイアウトパターン、コンポーネントライブラリ、情報の優先順位、使用上の注意',
    },
    {
      src: '/assets/migrated/1913da2a308b4b48c181.webp',
      caption: 'G17 UI / Accessibility / Motion — UI／アクセシビリティ／モーション / UIの基本原則、UIコンポーネント、アクセシビリティ基準、モーションの基本原則、使用例、Do／Don’t例',
    },
    {
      src: '/assets/migrated/853bffc096a759e275e5.webp',
      caption: 'G19 Digital Badge Design System — デジタルバッジデザインシステム / バッジの考え方、バッジ体系、バッジデザインルール、表示ルール、獲得・進化の表現、バッジ活用シーン',
    },
    {
      src: '/assets/migrated/3b6802984ab4f6ed79b9.webp',
      caption: 'G20 Advertising & Promotion Rule — 広告・プロモーションルール / 基本原則、ターゲット別コミュニケーションの視点、キーメッセージ例、プロモーション導線設計、ビジュアルスタイルルール、広告・プロモーション展開例、コピーライティングルール',
    },
    {
      src: '/assets/migrated/abb4662554f5ec6da249.webp',
      caption: 'G21 Store Tool / POP / Signage / QR — 店舗ツール・POP・サイン・QRルール / 基本原則、POP（店内販促ツール）、サイネージ（デジタル・店頭ディスプレイ）、サイン・掲示物、QRコードのルール、素材・仕様ガイド',
    },
    {
      src: '/assets/migrated/0250a3acc582f54d999d.webp',
      caption: 'G21-2 Store Experience Guideline — 店頭体験ガイドライン / 体験の流れ（Customer Journey）、店頭のタッチポイント、推奨ツール、デザインの基本ルール',
    },
    {
      src: '/assets/migrated/d72e1811df3c41ece921.webp',
      caption: 'G21-3 Store Communication Example — ストアコミュニケーション実例 / エントランスから店頭・レジ周り・店内訴求・キャンペーン・SNS・再来店までの実際の展開例と期待できる効果',
    },
    {
      src: '/assets/migrated/73e95e889a048538a793.webp',
      caption: 'G22 SNS / Banner / Video — SNS・バナー・動画ガイドライン / 基本原則、SNS投稿フォーマット、バナーサイズガイド、コピーライティングルール、動画の基本ルール、SNSブランド表現ルール',
    },
    {
      src: '/assets/migrated/2c9c70965a7e91f61ad8.webp',
      caption: 'G23 Web / App / CTA Rule — ウェブ・アプリ・CTAルール / 基本原則、Web/App画面構成ルール、CTA（コール・トゥ・アクション）ルール、ナビゲーションルール、バナー・カードデザインルール、フォーム・入力ルール、アクセシビリティ・ユーザビリティ',
    },
    {
      src: '/assets/migrated/cae72bd5b5229ab83800.webp',
      caption: 'G25 Creative Checklist / Governance / Summary — クリエイティブチェックリスト・ガバナンス・サマリー / 制作前チェックリスト、制作・承認プロセス、パートナー・外部制作会社へのガイドライン、ブランド価値の維持・向上に向けて',
    },
    {
      src: '/assets/migrated/85b3d8ff490e353f942c.webp',
      caption: 'Brand Message / Copy — ブランドメッセージ・コピー / ブランドアイデア「使うたびに、会津が動く。」、ブランドタグライン候補、ターゲット別コピー、ブランドキーワード',
    },
    {
      src: '/assets/migrated/b19b9606cb372bd8ddcc.webp',
      caption: 'Appendix A — Brand Copy Collection / ブランドコピー集 / コピー案一覧、活用シーン別マトリクス、トーン＆フィール、コピーライティングのポイント、使い方のヒント',
    },
    {
      src: '/assets/migrated/315939d646573b7e866e.webp',
      caption: 'END — ブランドは、ルールではなく、約束である。使うたびに、会津がもっと好きになる。AIZU COIN BRAND DESIGN GUIDELINE',
    },
    {
      src: '/assets/migrated/a132cda120a976b7fb66.webp',
      caption: '裏表紙 — BRAND DESIGN GUIDELINE / AIZU COIN ブランドコミュニケーションプラン',
    },
  ],
};

export default aizuCoinDesignGuidelineCase;